/**
 * Renewals tab: when each licence renews and what its margin becomes once Pax8's renewal price applies (#1112: "cost
 * now and renew cost and flags for both", so a price change goes out before the term rolls). A 12-month swimlane, one
 * row per customer (dot size = what we bill a month, colour = margin at renewal), above a month-grouped list.
 * Reproduces the interim claude.ai page's Renewals view.
 */
import { cn, EmptyState, Tooltip, useIsSinglePane } from '@patchbay/ui'
import { useQuery } from '@tanstack/react-query'
import * as React from 'react'
import type { MarginBand, ReconLineOut } from '../../lib/api/generated'
import { formatWhen } from '../../lib/display-time'
import { Tile } from '../profit/format'
import { QueryView } from '../profit/states'
import {
  BAND_RANK,
  bandOf,
  cad,
  cadCompact,
  customerLabel,
  DASH_CELL,
  DOT_TONE,
  dayLabel,
  daysFrom,
  ExtLink,
  FilterChips,
  type GroupedColumn,
  GroupedTable,
  MARGIN_BANDS,
  MARGIN_TONE,
  MarginValue,
  num,
  pax8Url,
  pct1,
  qty,
  reconQuery,
  SearchBox,
  signed,
  termLabel,
} from './common'
import { isBilled, matchesSearch, monthlyBilled, monthlyCost, total } from './licences-tab'
import { NotesButton } from './notes'

type Window = '30' | '90' | '365'
type RenewalFilter = 'all' | 'negative' | 'thin' | 'up' | 'noted'

const WINDOWS: { id: Window; label: string; days: number }[] = [
  { id: '30', label: 'Next 30 days', days: 30 },
  { id: '90', label: 'Next 90 days', days: 90 },
  { id: '365', label: 'Next 12 months', days: 366 },
]

/** A term commitment renews once a year; month-to-month lines renew monthly and are hidden unless asked for. */
export const isCommit = (r: ReconLineOut): boolean => /year|annual/i.test(r.commitment_term ?? '')
const costChange = (r: ReconLineOut): number | null => {
  const a = monthlyCost(r, 'now')
  const b = monthlyCost(r, 'renewal')
  return a === null || b === null ? null : b - a
}
const renewalBand = (r: ReconLineOut): MarginBand => (isBilled(r) ? bandOf(r.band_renewal, r.margin_pct_renewal) : 'unknown')
const dotValue = (r: ReconLineOut) => monthlyBilled(r) ?? monthlyCost(r, 'renewal') ?? 0

const FILTERS: { id: RenewalFilter; label: string; test: (r: ReconLineOut) => boolean }[] = [
  { id: 'all', label: 'All', test: () => true },
  { id: 'negative', label: 'Negative at renewal', test: (r) => renewalBand(r) === 'negative' },
  { id: 'thin', label: 'Under 10% at renewal', test: (r) => ['under_5', 'under_10'].includes(renewalBand(r)) },
  { id: 'up', label: 'Cost goes up', test: (r) => (costChange(r) ?? 0) > 0.05 },
  { id: 'noted', label: 'Has a note', test: (r) => (r.notes_count ?? 0) > 0 },
]

const monthKey = (iso: string) => iso.slice(0, 7)
const monthName = (key: string, long = false) => {
  const month = formatWhen(`${key}-01T12:00:00`, { style: 'date' }).split(' ')[0] ?? key
  const year = key.slice(0, 4)
  return `${month} ${long ? year : year.slice(2)}`
}

function summary(rows: ReconLineOut[]) {
  return {
    n: rows.length,
    billed: total(rows.filter(isBilled), monthlyBilled),
    cost: total(rows, (r) => monthlyCost(r, 'renewal')),
    up: total(rows, costChange),
    negative: rows.filter((r) => renewalBand(r) === 'negative').length,
  }
}

/** Hover lines for one licence: Pax8 price now → at renewal, margin now → at renewal. */
export function renewalTip(r: ReconLineOut): string[] {
  const lines = [`${qty(r.pax8_quantity)} × ${r.product_name ?? r.sku ?? 'licence'}`]
  const a = num(r.unit_cost_now)
  const b = num(r.unit_cost_renewal)
  if (a !== null && b !== null) {
    const d = b - a
    lines.push(
      Math.abs(d) < 0.005
        ? `Pax8 price ${cad(a)} a seat, no change`
        : `Pax8 price ${cad(a)} → ${cad(b)} a seat (${signed(d, cad)}${a ? `, ${signed((d / a) * 100, (x) => `${x.toFixed(1)}%`)}` : ''})`,
    )
    const ch = costChange(r)
    if (ch !== null && Math.abs(ch) > 0.05) lines.push(`Cost ${signed(ch, cad)} a month on ${qty(r.pax8_quantity)} seats`)
  }
  const m1 = num(r.margin_pct_now)
  const m2 = num(r.margin_pct_renewal)
  if (isBilled(r)) {
    lines.push(`We bill ${cad(monthlyBilled(r))} a month`)
    if (m1 !== null && m2 !== null) {
      lines.push(
        Math.abs(m2 - m1) < 0.05
          ? `Margin ${pct1(m1)}, no change`
          : `Margin ${pct1(m1)} → ${pct1(m2)} (${signed(m2 - m1, (x) => `${x.toFixed(1)} pts`)})`,
      )
    }
  }
  return lines
}

function Swimlane({
  rows,
  now,
  end,
  month,
  onMonth,
  onCustomer,
  onDot,
}: {
  rows: ReconLineOut[]
  now: Date
  end: Date
  month: string | null
  onMonth: (key: string | null) => void
  onCustomer: (name: string) => void
  onDot: (lineKey: string) => void
}) {
  const t0 = now.getTime()
  const span = end.getTime() - t0
  const x = (iso: string) => Math.min(100, Math.max(0, ((Date.parse(`${iso.slice(0, 10)}T12:00:00`) - t0) / span) * 100))
  const months: { key: string; a: number; b: number }[] = []
  const cursor = new Date(now.getFullYear(), now.getMonth(), 1)
  while (cursor.getTime() < end.getTime()) {
    const key = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`
    const a = Math.max(0, ((cursor.getTime() - t0) / span) * 100)
    cursor.setMonth(cursor.getMonth() + 1)
    months.push({ key, a, b: Math.min(100, ((cursor.getTime() - t0) / span) * 100) })
  }
  const byCustomer = new Map<string, ReconLineOut[]>()
  for (const r of rows) byCustomer.set(customerLabel(r), [...(byCustomer.get(customerLabel(r)) ?? []), r])
  const firstDate = (rs: ReconLineOut[]) => rs.map((r) => r.renewal_date ?? '').sort()[0] ?? ''
  const customers = [...byCustomer.entries()].sort((a, b) => firstDate(a[1]).localeCompare(firstDate(b[1])) || a[0].localeCompare(b[0]))
  const maxV = Math.max(1, ...rows.map(dotValue))
  const narrow = useIsSinglePane()
  if (narrow) {
    return (
      <div className="min-w-0 max-w-full space-y-3" data-testid="renewal-swimlane" data-layout="cards">
        <ul className="flex min-w-0 flex-wrap gap-2" aria-label="Renewal months">
          {months.map((m) => {
            const s = summary(rows.filter((r) => r.renewal_date && monthKey(r.renewal_date) === m.key))
            return (
              <li key={m.key} className="min-w-0 max-w-full">
                <button
                  type="button"
                  aria-pressed={month === m.key}
                  aria-label={`${monthName(m.key, true)}: ${s.n} renewals, ${cad(s.billed)} a month billed`}
                  onClick={() => onMonth(month === m.key ? null : m.key)}
                  className={cn(
                    'max-w-full cursor-pointer rounded-md border border-border px-2 py-1 text-left text-xs hover:bg-muted',
                    month === m.key && 'bg-primary-soft shadow-[inset_0_-2px_0_var(--color-primary)]',
                  )}
                >
                  <span className="block font-semibold">{monthName(m.key)}</span>
                  <span className="block break-words text-muted-foreground">{s.n ? `${s.n} · ${cadCompact(s.billed)}` : '–'}</span>
                  {(s.up ?? 0) > 0.5 ? <span className="block break-words text-destructive">+{cadCompact(s.up)} cost</span> : null}
                </button>
              </li>
            )
          })}
        </ul>
        {customers.length === 0 ? <p className="text-sm text-muted-foreground">Nothing renews in this window</p> : null}
        <ul className="min-w-0 space-y-3">
          {customers.map(([name, rs]) => {
            const byDay = new Map<string, ReconLineOut[]>()
            for (const r of rs)
              if (r.renewal_date) byDay.set(r.renewal_date.slice(0, 10), [...(byDay.get(r.renewal_date.slice(0, 10)) ?? []), r])
            return (
              <li key={name} className="min-w-0 rounded-lg border border-border bg-card p-3">
                <button
                  type="button"
                  title={name}
                  className="max-w-full cursor-pointer break-words text-left text-sm font-medium hover:text-primary"
                  onClick={() => onCustomer(name)}
                >
                  {name}
                </button>
                <ul className="mt-2 min-w-0 space-y-1">
                  {[...byDay.entries()].map(([day, ds]) => {
                    const v = ds.reduce((a, r) => a + dotValue(r), 0)
                    const worst = ds.map(renewalBand).sort((a, b) => BAND_RANK[a] - BAND_RANK[b])[0] ?? 'unknown'
                    const tip = [`${name} · ${dayLabel(day)} (in ${daysFrom(day, now)} days)`, ...ds.flatMap(renewalTip)]
                    const dim = month !== null && monthKey(day) !== month
                    return (
                      <li key={day}>
                        <button
                          type="button"
                          aria-label={tip.join('. ')}
                          data-band={worst}
                          onClick={() => ds[0] && onDot(ds[0].line_key)}
                          className={cn(
                            'flex w-full min-w-0 cursor-pointer items-start gap-2 rounded-md px-1 py-1 text-left hover:bg-muted',
                            dim && 'opacity-20',
                          )}
                        >
                          <span className={cn('mt-1 size-3 shrink-0 rounded-full', DOT_TONE[worst])} aria-hidden />
                          <span className="min-w-0 break-words text-sm">
                            <span className="font-medium">{dayLabel(day)}</span>
                            <span className="block text-xs text-muted-foreground">
                              in {daysFrom(day, now)} days · {cad(v)} a month
                            </span>
                            <span className="block text-xs">{ds.map((r) => r.product_name ?? r.sku ?? 'licence').join(', ')}</span>
                          </span>
                        </button>
                      </li>
                    )
                  })}
                </ul>
              </li>
            )
          })}
        </ul>
      </div>
    )
  }
  const grid = (
    <>
      {months.map((m) => (
        <div
          key={m.key}
          aria-hidden
          className={cn('pointer-events-none absolute inset-y-0 border-l border-border', month === m.key && 'bg-primary/5')}
          style={{ left: `${m.a}%`, width: `${m.b - m.a}%` }}
        />
      ))}
      <div aria-hidden className="pointer-events-none absolute inset-y-0 z-[1] border-l-2 border-primary" style={{ left: '0%' }} />
    </>
  )
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card" data-testid="renewal-swimlane">
      <div className="min-w-[760px]">
        <div className="sticky top-0 z-[2] grid grid-cols-[13rem_1fr] border-b border-border bg-card max-md:grid-cols-[8.5rem_1fr]">
          <div className="border-r border-border px-2.5 py-1.5 text-xs text-muted-foreground">
            {rows.length ? 'Customer' : 'Nothing renews in this window'}
          </div>
          <div className="relative min-h-[52px]">
            {months.map((m) => {
              const s = summary(rows.filter((r) => r.renewal_date && monthKey(r.renewal_date) === m.key))
              return (
                <button
                  key={m.key}
                  type="button"
                  aria-pressed={month === m.key}
                  aria-label={`${monthName(m.key, true)}: ${s.n} renewals, ${cad(s.billed)} a month billed`}
                  onClick={() => onMonth(month === m.key ? null : m.key)}
                  className={cn(
                    'absolute inset-y-0 flex cursor-pointer flex-col gap-px overflow-hidden border-l border-border px-1.5 py-1 text-left hover:bg-muted',
                    month === m.key && 'bg-primary-soft shadow-[inset_0_-2px_0_var(--color-primary)]',
                  )}
                  style={{ left: `${m.a}%`, width: `${m.b - m.a}%` }}
                >
                  {m.b - m.a < 4 ? null : <b className="text-xs font-semibold">{monthName(m.key)}</b>}
                  <span className="tabular truncate font-mono text-2xs text-muted-foreground">
                    {s.n ? `${s.n} · ${cadCompact(s.billed)}` : '–'}
                  </span>
                  {(s.up ?? 0) > 0.5 ? (
                    <span className="tabular truncate font-mono text-2xs text-destructive">+{cadCompact(s.up)} cost</span>
                  ) : null}
                </button>
              )
            })}
          </div>
        </div>
        {customers.map(([name, rs]) => {
          const byDay = new Map<string, ReconLineOut[]>()
          for (const r of rs)
            if (r.renewal_date) byDay.set(r.renewal_date.slice(0, 10), [...(byDay.get(r.renewal_date.slice(0, 10)) ?? []), r])
          return (
            <div key={name} className="grid grid-cols-[13rem_1fr] border-b border-border last:border-0 max-md:grid-cols-[8.5rem_1fr]">
              <div className="truncate border-r border-border px-2.5 py-1.5 text-xs">
                <button type="button" title={name} className="cursor-pointer hover:text-primary" onClick={() => onCustomer(name)}>
                  {name}
                </button>
              </div>
              <div className="relative min-h-[30px]">
                {grid}
                {[...byDay.entries()].map(([day, ds]) => {
                  const v = ds.reduce((a, r) => a + dotValue(r), 0)
                  const size = Math.round(12 + 20 * Math.sqrt(Math.min(1, v / maxV)))
                  const worst = ds.map(renewalBand).sort((a, b) => BAND_RANK[a] - BAND_RANK[b])[0] ?? 'unknown'
                  const tip = [`${name} · ${dayLabel(day)} (in ${daysFrom(day, now)} days)`, ...ds.flatMap(renewalTip)]
                  const dim = month !== null && monthKey(day) !== month
                  return (
                    <Tooltip
                      key={day}
                      content={
                        <span className="block max-w-80 space-y-0.5 py-0.5">
                          {tip.map((line, i) => (
                            // biome-ignore lint/suspicious/noArrayIndexKey: tooltip lines are positional.
                            <span key={i} className="block">
                              {line}
                            </span>
                          ))}
                        </span>
                      }
                    >
                      <button
                        type="button"
                        aria-label={tip.join('. ')}
                        data-band={worst}
                        onClick={() => ds[0] && onDot(ds[0].line_key)}
                        className={cn(
                          'absolute top-1/2 z-[1] -translate-x-1/2 -translate-y-1/2 cursor-pointer rounded-full border-2 border-card hover:outline-2 hover:outline-foreground focus-visible:outline-2 focus-visible:outline-foreground',
                          DOT_TONE[worst],
                          dim && 'opacity-20',
                        )}
                        style={{ left: `${x(day)}%`, width: size, height: size }}
                      />
                    </Tooltip>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function columns(now: Date): GroupedColumn<ReconLineOut>[] {
  return [
    {
      id: 'renews',
      header: 'Renews',
      group: '',
      sortValue: (r) => r.renewal_date ?? null,
      cell: (r) => {
        const d = daysFrom(r.renewal_date, now)
        return (
          <span className="whitespace-nowrap">
            {dayLabel(r.renewal_date)}
            <span className="block text-xs text-muted-foreground">{d === 0 ? 'today' : `in ${d} day${d === 1 ? '' : 's'}`}</span>
          </span>
        )
      },
    },
    {
      id: 'customer',
      header: 'Customer',
      group: '',
      sortValue: customerLabel,
      cell: (r) => (
        <ExtLink href={r.links?.crm} className="font-medium">
          {customerLabel(r)}
        </ExtLink>
      ),
    },
    {
      id: 'licence',
      header: 'Licence',
      group: '',
      sortValue: (r) => r.product_name ?? '',
      cell: (r) => (
        <div className="min-w-40">
          <ExtLink href={pax8Url(r.pax8_company_id, r.links?.pax8)}>{r.product_name ?? r.sku}</ExtLink>
          <span className="block text-xs text-muted-foreground">{termLabel(r)}</span>
        </div>
      ),
    },
    { id: 'seats', header: 'Seats', group: '', numeric: true, cell: (r) => qty(r.pax8_quantity), sortValue: (r) => num(r.pax8_quantity) },
    {
      id: 'billed',
      header: 'We bill',
      group: 'Per month',
      numeric: true,
      sortValue: (r) => (isBilled(r) ? monthlyBilled(r) : null),
      cell: (r) => (isBilled(r) ? <ExtLink href={r.links?.books_invoice}>{cad(monthlyBilled(r))}</ExtLink> : DASH_CELL),
    },
    {
      id: 'cost_now',
      header: 'Cost now',
      group: 'Per month',
      numeric: true,
      cell: (r) => cad(monthlyCost(r, 'now')),
      sortValue: (r) => monthlyCost(r, 'now'),
    },
    {
      id: 'cost_renewal',
      header: 'At renewal',
      group: 'Per month',
      numeric: true,
      cell: (r) => cad(monthlyCost(r, 'renewal')),
      sortValue: (r) => monthlyCost(r, 'renewal'),
    },
    {
      id: 'change',
      header: 'Change',
      group: 'Per month',
      numeric: true,
      sortValue: costChange,
      cell: (r) => {
        const ch = costChange(r)
        if (ch === null || Math.abs(ch) <= 0.05) return DASH_CELL
        return <span className={ch > 0 ? 'text-destructive' : 'text-success'}>{signed(ch, cad)}</span>
      },
    },
    {
      id: 'margin',
      header: 'Margin',
      group: 'At renewal',
      numeric: true,
      sortValue: (r) => (isBilled(r) ? num(r.margin_pct_renewal) : null),
      cell: (r) =>
        isBilled(r) ? (
          <MarginValue
            when={`From ${dayLabel(r.renewal_date)}`}
            billed={r.billed_rate}
            cost={r.unit_cost_renewal}
            margin={r.margin_pct_renewal}
            band={r.band_renewal}
            seats={num(r.billed_quantity)}
            unit="per seat per month"
          />
        ) : (
          DASH_CELL
        ),
    },
    {
      id: 'commit',
      header: '12-mo commit',
      group: 'At renewal',
      numeric: true,
      narrow: true,
      sortValue: (r) => (isCommit(r) ? (monthlyCost(r, 'renewal') ?? 0) * 12 : null),
      cell: (r) => (isCommit(r) ? cad((monthlyCost(r, 'renewal') ?? 0) * 12) : DASH_CELL),
    },
    {
      id: 'notes',
      header: 'Notes',
      group: ' ',
      cell: (r) => (
        <NotesButton
          lineKey={r.line_key}
          count={r.notes_count ?? 0}
          title={`${customerLabel(r)} · ${r.product_name ?? 'licence'}`}
          subtitle={termLabel(r)}
        />
      ),
    },
  ]
}

export function RenewalsTab() {
  const q = useQuery(reconQuery())
  const now = React.useMemo(() => new Date(), [])
  const [win, setWin] = React.useState<Window>('365')
  const [filter, setFilter] = React.useState<RenewalFilter>('all')
  const [monthToMonth, setMonthToMonth] = React.useState(false)
  const [month, setMonth] = React.useState<string | null>(null)
  const [search, setSearch] = React.useState('')
  const [flash, setFlash] = React.useState<string | null>(null)
  const cols = React.useMemo(() => columns(now), [now])
  const jumpTo = (lineKey: string) => {
    setMonth(null)
    setFilter('all')
    setFlash(lineKey)
    requestAnimationFrame(() =>
      document.querySelector(`tr[data-line="${CSS.escape(lineKey)}"]`)?.scrollIntoView({ block: 'center', behavior: 'smooth' }),
    )
  }
  return (
    <QueryView query={q} what="Renewals">
      {(data) => {
        const days = WINDOWS.find((w) => w.id === win)?.days ?? 366
        const end = new Date(now.getTime() + days * 86_400_000)
        const all = (data.lines ?? []).filter((r) => {
          if (r.kind !== 'licence' || !r.renewal_date) return false
          const d = daysFrom(r.renewal_date, now)
          return d !== null && d >= 0 && d < days && (isCommit(r) || monthToMonth)
        })
        const test = FILTERS.find((f) => f.id === filter)?.test ?? (() => true)
        const shown = all.filter((r) => test(r) && matchesSearch(r, search))
        const listed = shown.filter((r) => !month || (r.renewal_date && monthKey(r.renewal_date) === month))
        const in30 = summary(all.filter((r) => (daysFrom(r.renewal_date, now) ?? 999) < 30))
        const in90 = summary(all.filter((r) => (daysFrom(r.renewal_date, now) ?? 999) < 90))
        const sAll = summary(all)
        return (
          <div className="space-y-3.5">
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-6">
              <Tile label="Renewing in 30 days" value={in30.n} sub={`${cadCompact(in30.billed)} / mo billed`} />
              <Tile label="Renewing in 90 days" value={in90.n} sub={`${cadCompact(in90.billed)} / mo billed`} />
              <Tile
                label="Cost increase at renewal"
                value={sAll.up === null ? '—' : `${signed(sAll.up, (v) => cadCompact(v))}/mo`}
                tone={(sAll.up ?? 0) > 0 ? 'warning' : undefined}
              />
              <Tile label="Negative at renewal" value={sAll.negative} tone={sAll.negative ? 'danger' : undefined} />
              <Tile
                label="12-mo commitments in view"
                value={cadCompact(total(all.filter(isCommit), (r) => ((c) => (c === null ? null : c * 12))(monthlyCost(r, 'renewal'))))}
              />
              <Tile label="Billed / mo in view" value={cadCompact(sAll.billed)} />
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <FilterChips
                label="Renewal window"
                value={win}
                onChange={(w) => {
                  setWin(w)
                  setMonth(null)
                }}
                options={WINDOWS.map((w) => ({ id: w.id, label: w.label }))}
              />
              <FilterChips
                label="Renewal filters"
                value={filter}
                onChange={setFilter}
                options={FILTERS.map((f) => ({ id: f.id, label: f.label, count: all.filter(f.test).length }))}
              />
              <label className="flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground">
                <input type="checkbox" checked={monthToMonth} onChange={(e) => setMonthToMonth(e.currentTarget.checked)} />
                Also show month-to-month
              </label>
            </div>
            <ul aria-label="Swimlane legend" className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
              <li className="mr-0.5">Margin at renewal</li>
              {MARGIN_BANDS.map((b) => (
                <li key={b.band} className={cn('rounded-sm px-1.5 py-px text-2xs font-medium leading-5', MARGIN_TONE[b.band])}>
                  {b.label}
                </li>
              ))}
              <li className="ml-3">Dot size is what we bill a month. Click a month to list just that month, a dot to find its line.</li>
            </ul>
            <Swimlane rows={shown} now={now} end={end} month={month} onMonth={setMonth} onCustomer={setSearch} onDot={jumpTo} />
            <SearchBox
              value={search}
              onChange={setSearch}
              label="Search customer, licence"
              count={`${listed.length} renewals${month ? ` in ${monthName(month, true)} · click the month again to show all` : ''}`}
            />
            <div className="min-w-0 max-w-full">
              <GroupedTable
                label="Renewals"
                columns={cols}
                rows={listed}
                rowKey={(r) => r.line_key}
                initialSort={{ id: 'renews', dir: 1 }}
                rowProps={(r) => ({ 'data-line': r.line_key, className: flash === r.line_key ? 'bg-primary-soft' : undefined })}
                renderBefore={(r, i, sorted, sort) => {
                  if (sort?.id !== 'renews' || !r.renewal_date) return null
                  const key = monthKey(r.renewal_date)
                  const prev = sorted[i - 1]?.renewal_date
                  if (prev && monthKey(prev) === key) return null
                  const s = summary(sorted.filter((x) => x.renewal_date && monthKey(x.renewal_date) === key))
                  return (
                    <tr className="bg-muted">
                      <td colSpan={cols.length} className="px-2 py-1.5 text-xs font-semibold">
                        {monthName(key, true)}
                        <span className="ml-2 font-normal text-muted-foreground">
                          {s.n} renewal{s.n === 1 ? '' : 's'} · {cad(s.billed)}/mo billed · cost {cad(s.cost)}/mo at renewal
                          {(s.up ?? 0) > 0.05 ? ` (+${cad(s.up)})` : ''}
                          {s.negative ? ` · ${s.negative} negative` : ''}
                        </span>
                      </td>
                    </tr>
                  )
                }}
                empty={<EmptyState title="Nothing renews in this window." />}
              />
            </div>
          </div>
        )
      }}
    </QueryView>
  )
}
